<template>
  <CustomDashboard
    :user-id="userId"
    :dashboard="dashboard"
    :dashboard-owners="dashboardOwners"
    :export-data="exportData"
  />
</template>

<script>
import axios from 'axios'
import CustomDashboard from '@/components/CustomDashboard'
import { getData } from '@/helpers/custom-dashboard'

export default {
  components: {
    CustomDashboard
  },

  props: {
    userId: Number
  },

  data () {
    return {
      context: 'dashboards',
      dashboard: DASHBOARD,
      queryLimit: 100000, // Limit for the number of results returned.
      dashboardId: 58,
      dashboardOwners: ''
    }
  },

  computed: {
    query () {
      let query = `SELECT * FROM (SELECT * FROM ${this.dashboard.dataSource} WHERE `
      this.dashboard.widgets.forEach(widget => {
        if (widget.metadata.customField || widget.metadata.type === 'table') {
          return
        }

        if (['checkbox', 'select-multiple'].includes(widget.metadata.type)) {
          query += `${widget.metadata.name} IN ('${widget.value.join('\', \'')}') AND `
        } else if (['select', 'input'].includes(widget.metadata.type)) {
          query += `${widget.metadata.name} = '${widget.value}' AND `
        }
      })
      query += `1=1 ORDER BY ${this.dashboard.widgets[0].headerColumn.name} DESC LIMIT ${this.dashboard.widgets[5].value}) ORDER BY ${this.dashboard.widgets[0].headerColumn.name}`
      return query
    },

    exportData () {
      return this.dashboard.widgets.filter(widget => widget.metadata.type === 'table')
        .map(widget => ({ name: widget.metadata.title, data: widget.value }))
    },

    regionQuery () {
      let query = `SELECT DISTINCT REGION_NAME FROM ${this.dashboard.dataSource} WHERE `
      this.dashboard.widgets[4].value.forEach(value => (query += `(${this.dashboard.widgets[4].clause[value]}) OR `))
      query += '1=0 ORDER BY REGION_NAME ASC'
      return query
    }
  },

  watch: {
    query (val) {
      this.dashboard.widgets[0].loading = true
      this.dashboard.widgets[10].loading = true
      this.dashboard.widgets[11].loading = true
      this.dashboard.widgets[12].loading = true
      this.dashboard.widgets[13].loading = true
      this.dashboard.widgets[14].loading = true

      getData(val, this.queryLimit).then(result => {
        this.$dashboardQueryLog(this.dashboard.title.name, result.data.queryId, val, 'overviewQuery')
        if (result.data.error) {
          this.dashboard.widgets[0].loading = false
          this.dashboard.widgets[10].loading = false
          this.dashboard.widgets[11].loading = false
          this.dashboard.widgets[12].loading = false
          this.dashboard.widgets[13].loading = false
          this.dashboard.widgets[14].loading = false
          return
        }
        this.dashboard.widgets[0].value = Object.freeze(result.data.data)
        this.dashboard.widgets[10].value = Object.freeze(result.data.data)
        this.dashboard.widgets[11].value = Object.freeze(result.data.data)
        this.dashboard.widgets[12].value = Object.freeze(result.data.data)
        this.dashboard.widgets[13].value = Object.freeze(result.data.data)
        this.dashboard.widgets[14].value = Object.freeze(result.data.data)
        this.dashboard.widgets[0].loading = false
        this.dashboard.widgets[10].loading = false
        this.dashboard.widgets[11].loading = false
        this.dashboard.widgets[12].loading = false
        this.dashboard.widgets[13].loading = false
        this.dashboard.widgets[14].loading = false
      })
    },

    regionQuery (val) {
      this.dashboard.widgets[3].loading = true
      getData(val, this.queryLimit).then(result => {
        this.$dashboardQueryLog(this.dashboard.title.name, result.data.queryId, val, 'regionQuery')
        if (result.data.error) {
          this.dashboard.widgets[3].loading = false
          return
        }
        this.dashboard.widgets[3].options = Object.freeze(result.data.data.map(option => ({
          name: option[this.dashboard.widgets[3].metadata.name],
          value: option[this.dashboard.widgets[3].metadata.name]
        })))
        if (!this.dashboard.widgets[3].options.find(element => element.value === this.dashboard.widgets[3].value)) {
          this.dashboard.widgets[3].value = null
        }
        this.dashboard.widgets[3].loading = false
      })
    },

    dashboard: {
      deep: true,
      handler (dashboard) {
        const queryParams = dashboard.widgets
          .filter(widget => !['table', 'table-by-value', 'text', 'blank', 'report'].includes(widget.metadata.type))
          .reduce((acc, cur) => {
            acc[cur.metadata.name.toLowerCase()] = cur.value ? cur.value.toString() : ''
            return acc
          }, {})
        this.$router.replace({ name: this.$route.name, query: queryParams })
      }
    }
  },

  beforeCreate () {
    sessionStorage.setItem('context', 'dashboards')
  },

  created () {
    axios.get(`${process.env.VUE_APP_API_URI}/dashboards/${this.dashboardId}`)
      .then(response => {
        const dashboards = [...response.data]
        if (dashboards.length) {
          const sharedDashboard = { ...dashboards[0] }
          this.dashboardOwners = sharedDashboard.owners
        }
      })

    this.dashboard.widgets
      .filter(widget => ['table', 'table-by-value', 'text', 'blank', 'report'].every(value => value.indexOf(widget.metadata.type) < 0) && !widget.metadata.customField)
      .forEach(widget => getData(widget.query, this.queryLimit).then(result => {
        this.$dashboardQueryLog(this.dashboard.title.name, result.data.queryId, widget.query, `${widget.metadata.name}Query`)
        if (['select', 'select-multiple'].includes(widget.metadata.type)) {
          return (widget.options = result.data.data.map(option => ({
            name: option[widget.metadata.name],
            value: option[widget.metadata.name]
          })))
        } else {
          return (widget.options = result.data.data.map(option => option[widget.metadata.name]))
        }
      }))

    // backfilling filters from URL params
    Object.keys(this.$route.query).map(param => {
      this.dashboard.widgets.forEach(filter => {
        if (filter.metadata.name && filter.metadata.name.toLowerCase() === param) {
          filter.value = this.$route.query[param]
        }
      })
    })

    // Make sure checkbox types are array not string
    this.dashboard.widgets.forEach(filter => {
      if (['checkbox', 'select-multiple'].includes(filter.metadata.type) && typeof filter.value === 'string') {
        filter.value = filter.value.split(',')
      }
    })

    this.dashboard.widgets[0].loading = true
    this.dashboard.widgets[10].loading = true
    this.dashboard.widgets[11].loading = true
    this.dashboard.widgets[12].loading = true
    this.dashboard.widgets[13].loading = true
    this.dashboard.widgets[14].loading = true

    getData(this.query, this.queryLimit).then(result => {
      this.$dashboardQueryLog(this.dashboard.title.name, result.data.queryId, this.query, 'overviewQuery')
      this.dashboard.widgets[0].value = Object.freeze(result.data.data)
      this.dashboard.widgets[10].value = Object.freeze(result.data.data)
      this.dashboard.widgets[11].value = Object.freeze(result.data.data)
      this.dashboard.widgets[12].value = Object.freeze(result.data.data)
      this.dashboard.widgets[13].value = Object.freeze(result.data.data)
      this.dashboard.widgets[14].value = Object.freeze(result.data.data)
      this.dashboard.widgets[0].loading = false
      this.dashboard.widgets[10].loading = false
      this.dashboard.widgets[11].loading = false
      this.dashboard.widgets[12].loading = false
      this.dashboard.widgets[13].loading = false
      this.dashboard.widgets[14].loading = false
    })

    this.$log('report_open', this.dashboard.title.name)
  }
}

const DASHBOARD = {
  dashboardName: 'Local Ops Dashboard Overview',
  title: {
    name: 'Local Overview',
    isShown: false
  },
  dataSource: 'PRODUCTION_DATA.DM.LOCAL_OPS_MERGED_2',
  widgets: [{
    x: 0,
    y: 0,
    w: 20,
    h: 17,
    i: 0,
    metadata: {
      name: 'growthTable',
      title: 'Growth Table',
      type: 'table',
      customField: false,
      tableType: 'horizontal',
      scroll: true
    },
    headerColumn: {
      name: 'CALENDAR_DATE',
      displayName: 'Calendar Date',
      format: 'date',
      fromFormat: 'YYYY-MM-DD',
      toFormat: 'MM/DD (ddd)'
    },
    columns: {
      COMPLETED_TRIPS: {
        displayName: 'Trips',
        format: 'number',
        tooltip: [{
          column: 'WOW_COMPLETED_TRIPS',
          displayName: '%WOW',
          format: 'percentage'
        }],
        background: {
          comparedColumn: 'WOW_COMPLETED_TRIPS',
          valueRange: [-0.05, 0.05],
          colorRange: ['F1BFC1', 'C5DABF']
        },
        styles: {
          backgroundColor: 'rgb(230, 236, 240)'
        }
      },
      GROSS_REVENUE: {
        displayName: 'Gross Bookings',
        format: 'currency',
        tooltip: [{
          column: 'WOW_GROSS_REVENUE',
          displayName: '%WOW',
          format: 'percentage'
        }],
        background: {
          comparedColumn: 'WOW_GROSS_REVENUE',
          valueRange: [-0.05, 0.05],
          colorRange: ['F1BFC1', 'C5DABF']
        },
        styles: {
          backgroundColor: 'rgb(230, 236, 240)'
        }
      },
      REFUND_OF_GROSS_REVENUE: {
        displayName: 'Refund as % Gross',
        format: 'percentage',
        tooltip: [{
          column: 'WOW_REFUND_OF_GROSS_REVENUE',
          displayName: '%WOW',
          format: 'percentage'
        }],
        background: {
          comparedColumn: 'WOW_REFUND_OF_GROSS_REVENUE',
          valueRange: [-0.05, 0.05],
          colorRange: ['C5DABF', 'F1BFC1']
        },
        styles: {
          backgroundColor: 'rgb(230, 236, 240)'
        }
      },
      UNCOLLECTED_OF_GROSS_REVENUE: {
        displayName: 'Failed Payment as % Gross',
        format: 'percentage',
        tooltip: [{
          column: 'WOW_UNCOLLECTED_OF_GROSS_REVENUE',
          displayName: '%WOW',
          format: 'percentage'
        }],
        background: {
          comparedColumn: 'WOW_UNCOLLECTED_OF_GROSS_REVENUE',
          valueRange: [-0.05, 0.05],
          colorRange: ['C5DABF', 'F1BFC1']
        },
        styles: {
          backgroundColor: 'rgb(230, 236, 240)'
        }
      },
      REFERRAL_PROMOTION: {
        displayName: 'Referral Promo Spend',
        format: 'currency',
        tooltip: [{
          column: 'WOW_REFERRAL_PROMOTION',
          displayName: '%WOW',
          format: 'percentage'
        }],
        background: {
          comparedColumn: 'WOW_REFERRAL_PROMOTION',
          valueRange: [-0.05, 0.05],
          colorRange: ['C5DABF', 'F1BFC1']
        },
        styles: {
          backgroundColor: 'rgb(230, 236, 240)'
        }
      },
      REFERRAL_OF_GROSS_REVENUE: {
        displayName: 'Referral Promo as % Gross',
        format: 'percentage',
        tooltip: [{
          column: 'WOW_REFERRAL_OF_GROSS_REVENUE',
          displayName: '%WOW',
          format: 'percentage'
        }],
        background: {
          comparedColumn: 'WOW_REFERRAL_OF_GROSS_REVENUE',
          valueRange: [-0.05, 0.05],
          colorRange: ['C5DABF', 'F1BFC1']
        },
        styles: {
          backgroundColor: 'rgb(230, 236, 240)'
        }
      },
      LIFECYCLE_PROMOTION: {
        displayName: 'Lifecycle Promo Spend',
        format: 'currency',
        tooltip: [{
          column: 'WOW_LIFECYCLE_PROMOTION',
          displayName: '%WOW',
          format: 'percentage'
        }],
        background: {
          comparedColumn: 'WOW_LIFECYCLE_PROMOTION',
          valueRange: [-0.05, 0.05],
          colorRange: ['C5DABF', 'F1BFC1']
        },
        styles: {
          backgroundColor: 'rgb(230, 236, 240)'
        }
      },
      LIFECYCLE_OF_GROSS_REVENUE: {
        displayName: 'Lifecycle Promo as % Gross',
        format: 'percentage',
        tooltip: [{
          column: 'WOW_LIFECYCLE_OF_GROSS_REVENUE',
          displayName: '%WOW',
          format: 'percentage'
        }],
        background: {
          comparedColumn: 'WOW_LIFECYCLE_OF_GROSS_REVENUE',
          valueRange: [-0.05, 0.05],
          colorRange: ['C5DABF', 'F1BFC1']
        },
        styles: {
          backgroundColor: 'rgb(230, 236, 240)'
        }
      },
      SELF_SERVE_PROMOTION: {
        displayName: 'Self-serve Promo Spend',
        format: 'currencyDecimal',
        tooltip: [{
          column: 'WOW_SELF_SERVE_PROMOTION',
          displayName: '%WOW',
          format: 'percentage'
        }],
        background: {
          comparedColumn: 'WOW_SELF_SERVE_PROMOTION',
          valueRange: [-0.05, 0.05],
          colorRange: ['C5DABF', 'F1BFC1']
        },
        styles: {
          backgroundColor: 'rgb(230, 236, 240)'
        }
      },
      SELF_SERVE_OF_GROSS_REVENUE: {
        displayName: 'Self-serve Promo as % Gross',
        format: 'percentage',
        tooltip: [{
          column: 'WOW_SELF_SERVE_OF_GROSS_REVENUE',
          displayName: '%WOW',
          format: 'percentage'
        }],
        background: {
          comparedColumn: 'WOW_SELF_SERVE_OF_GROSS_REVENUE',
          valueRange: [-0.05, 0.05],
          colorRange: ['C5DABF', 'F1BFC1']
        },
        styles: {
          backgroundColor: 'rgb(230, 236, 240)'
        }
      }
    },
    additionalColumns: [
      {
        name: 'Growth',
        span: 10,
        styles: {
          backgroundColor: 'rgb(230, 236, 240)'
        }
      }
    ],
    chart: {
      type: 'line',
      width: 150,
      xAxis: 'CALENDAR_DATE'
    },
    value: []
  }, {
    x: 20,
    y: 0,
    w: 4,
    h: 4,
    i: 1,
    metadata: {
      name: 'PERIOD',
      title: 'Period',
      type: 'select',
      customField: false
    },
    query: 'SELECT DISTINCT PERIOD FROM PRODUCTION_DATA.DM.LOCAL_OPS_MERGED_2 ORDER BY PERIOD ASC',
    value: 'Weekly',
    options: []
  }, {
    x: 20,
    y: 4,
    w: 4,
    h: 4,
    i: 2,
    metadata: {
      name: 'PRODUCT',
      title: 'Product',
      type: 'select',
      customField: false
    },
    query: 'SELECT DISTINCT PRODUCT FROM PRODUCTION_DATA.DM.LOCAL_OPS_MERGED_2 ORDER BY PRODUCT ASC',
    value: 'All Products',
    options: []
  }, {
    x: 20,
    y: 8,
    w: 4,
    h: 4,
    i: 3,
    metadata: {
      name: 'REGION_NAME',
      title: 'Region',
      type: 'select',
      customField: false
    },
    query: 'SELECT DISTINCT REGION_NAME FROM PRODUCTION_DATA.DM.LOCAL_OPS_MERGED_2 WHERE (REGION_NAME LIKE \'%Megaregion%\') OR (REGION_NAME LIKE \'%All Subregions%\') OR 1=0 ORDER BY REGION_NAME ASC',
    value: 'Megaregion - All Megaregions',
    options: []
  }, {
    x: 20,
    y: 12,
    w: 4,
    h: 10,
    i: 4,
    metadata: {
      name: 'REGION_FILTER',
      title: 'Regions to Show in Filter',
      type: 'checkbox',
      customField: true
    },
    value: ['Megaregion', 'Region'],
    options: ['Core Region', 'Megaregion', 'Region', 'Subregion'],
    clause: {
      'Core Region': 'REGION_NAME LIKE \'%Core Region%\'',
      Megaregion: 'REGION_NAME LIKE \'%Megaregion%\'',
      Region: 'REGION_NAME LIKE \'%All Subregions%\'',
      Subregion: 'REGION_NAME NOT LIKE \'%Core Region%\' AND REGION_NAME NOT LIKE \'%Megaregion%\' AND REGION_NAME NOT LIKE \'%All Subregions%\''
    }
  }, {
    x: 20,
    y: 22,
    w: 4,
    h: 4,
    i: 5,
    metadata: {
      name: 'NUMBER_FILTER',
      title: '# of Periods',
      type: 'input-number',
      customField: true
    },
    value: 8
  }, {
    x: 20,
    y: 32,
    w: 4,
    h: 6,
    i: 6,
    metadata: {
      type: 'text',
      customField: true
    },
    styles: {
      fontWeight: 'bold',
      backgroundColor: 'rgb(239, 235, 232)'
    },
    value: '* Decay, Juicer and UE metrics are currently not available at the subregion level. <br><br> ** UE metric is also not available at the daily level'
  }, {
    x: 20,
    y: 42,
    w: 4,
    h: 2,
    i: 7,
    metadata: {
      type: 'report',
      customField: true
    }
  }, {
    x: 20,
    y: 30,
    w: 4,
    h: 4,
    i: 8,
    metadata: {
      type: 'blank',
      customField: true
    }
  }, {
    x: 20,
    y: 40,
    w: 4,
    h: 4,
    i: 9,
    metadata: {
      type: 'blank',
      customField: true
    }
  }, {
    x: 0,
    y: 19,
    w: 20,
    h: 11,
    i: 10,
    metadata: {
      name: 'utilizationTable',
      title: 'Utilization Table',
      type: 'table',
      customField: false,
      tableType: 'horizontal',
      scroll: true
    },
    headerColumn: {
      name: 'CALENDAR_DATE',
      displayName: 'Calendar Date',
      format: 'date',
      fromFormat: 'YYYY-MM-DD',
      toFormat: 'MM/DD (ddd)'
    },
    columns: {
      TVD: {
        displayName: 'TVD',
        format: 'decimal',
        tooltip: [{
          column: 'WOW_TVD',
          displayName: '%WOW',
          format: 'percentage'
        }],
        background: {
          comparedColumn: 'WOW_TVD',
          valueRange: [-0.05, 0.05],
          colorRange: ['F1BFC1', 'C5DABF']
        },
        styles: {
          backgroundColor: 'rgb(245, 234, 215)'
        }
      },
      RPT: {
        displayName: 'RPT',
        format: 'currencyDecimal',
        tooltip: [{
          column: 'WOW_RPT',
          displayName: '%WOW',
          format: 'percentage'
        }],
        background: {
          comparedColumn: 'WOW_RPT',
          valueRange: [-0.05, 0.05],
          colorRange: ['F1BFC1', 'C5DABF']
        },
        styles: {
          backgroundColor: 'rgb(245, 234, 215)'
        }
      },
      MEDIAN_DURATION_IN_MINS: {
        displayName: 'Median Trip Time (in minutes)',
        format: 'number',
        tooltip: [{
          column: 'WOW_MEDIAN_DURATION_IN_MINS',
          displayName: '%WOW',
          format: 'percentage'
        }],
        background: {
          comparedColumn: 'WOW_MEDIAN_DURATION_IN_MINS',
          valueRange: [-0.05, 0.05],
          colorRange: ['F1BFC1', 'C5DABF']
        },
        styles: {
          backgroundColor: 'rgb(245, 234, 215)'
        }
      },
      MEDIAN_DISTANCE_IN_MILES: {
        displayName: 'Median Trip Distance (in miles)',
        format: 'decimal',
        tooltip: [{
          column: 'WOW_MEDIAN_DISTANCE_IN_MILES',
          displayName: '%WOW',
          format: 'percentage'
        }],
        background: {
          comparedColumn: 'WOW_MEDIAN_DISTANCE_IN_MILES',
          valueRange: [-0.05, 0.05],
          colorRange: ['F1BFC1', 'C5DABF']
        },
        styles: {
          backgroundColor: 'rgb(245, 234, 215)'
        }
      },
      RVD: {
        displayName: 'RVD',
        format: 'currencyDecimal',
        tooltip: [{
          column: 'WOW_RVD',
          displayName: '%WOW',
          format: 'percentage'
        }],
        background: {
          comparedColumn: 'WOW_RVD',
          valueRange: [-0.05, 0.05],
          colorRange: ['F1BFC1', 'C5DABF']
        },
        styles: {
          backgroundColor: 'rgb(245, 234, 215)'
        }
      },
      ZERO_RATE: {
        displayName: 'Zero Rate',
        format: 'percentage',
        precision: 1,
        tooltip: [{
          column: 'WOW_ZERO_RATE',
          displayName: '%WOW',
          format: 'percentage'
        }],
        background: {
          comparedColumn: 'WOW_ZERO_RATE',
          valueRange: [-0.05, 0.05],
          colorRange: ['C5DABF', 'F1BFC1']
        },
        styles: {
          backgroundColor: 'rgb(245, 234, 215)'
        }
      }
    },
    additionalColumns: [
      {
        name: 'Utilization',
        span: 6,
        styles: {
          backgroundColor: 'rgb(245, 234, 215)'
        }
      }
    ],
    chart: {
      type: 'line',
      width: 150,
      xAxis: 'CALENDAR_DATE'
    },
    value: []
  }, {
    x: 0,
    y: 32,
    w: 20,
    h: 15.5,
    i: 11,
    metadata: {
      name: 'fleetHealthTable',
      title: 'Fleet Health Table',
      type: 'table',
      customField: false,
      tableType: 'horizontal',
      scroll: true
    },
    headerColumn: {
      name: 'CALENDAR_DATE',
      displayName: 'Calendar Date',
      format: 'date',
      fromFormat: 'YYYY-MM-DD',
      toFormat: 'MM/DD (ddd)'
    },
    columns: {
      OPS_DECAY_RATE: {
        displayName: 'Decay Rate *',
        format: 'percentage',
        tooltip: [{
          column: 'WOW_DECAY_RATE',
          displayName: '%WOW',
          format: 'percentage'
        }],
        background: {
          comparedColumn: 'WOW_OPS_DECAY_RATE',
          valueRange: [-0.05, 0.05],
          colorRange: ['C5DABF', 'F1BFC1']
        },
        styles: {
          backgroundColor: 'rgb(232, 237, 218)'
        }
      },
      NET_OPS_DECAY_RATE: {
        displayName: 'Net Ops Decay Rate *',
        format: 'percentage',
        tooltip: [{
          column: 'WOW_OPS_DECAY_RATE',
          displayName: '%WOW',
          format: 'percentage'
        }],
        background: {
          comparedColumn: 'WOW_NET_OPS_DECAY_RATE',
          valueRange: [-0.05, 0.05],
          colorRange: ['C5DABF', 'F1BFC1']
        },
        styles: {
          backgroundColor: 'rgb(232, 237, 218)'
        }
      },
      SICK_RATE: {
        displayName: 'Sick Rate *',
        format: 'percentage',
        tooltip: [{
          column: 'WOW_SICK_RATE',
          displayName: '%WOW',
          format: 'percentage'
        }],
        background: {
          comparedColumn: 'WOW_SICK_RATE',
          valueRange: [-0.05, 0.05],
          colorRange: ['C5DABF', 'F1BFC1']
        },
        styles: {
          backgroundColor: 'rgb(232, 237, 218)'
        }
      },
      CUMULATIVE_NET: {
        displayName: 'Total Vehicle Count *',
        format: 'number',
        tooltip: [{
          column: 'WOW_CUMULATIVE_NET',
          displayName: '%WOW',
          format: 'percentage'
        }],
        background: {
          comparedColumn: 'WOW_CUMULATIVE_NET',
          valueRange: [-0.05, 0.05],
          colorRange: ['F1BFC1', 'C5DABF']
        },
        styles: {
          backgroundColor: 'rgb(232, 237, 218)'
        }
      },
      OPERATIONAL_VEHICLES: {
        displayName: 'Operational Vehicles *',
        format: 'number',
        tooltip: [{
          column: 'WOW_OPERATIONAL_VEHICLES',
          displayName: '%WOW',
          format: 'percentage'
        }],
        background: {
          comparedColumn: 'WOW_OPERATIONAL_VEHICLES',
          valueRange: [-0.05, 0.05],
          colorRange: ['F1BFC1', 'C5DABF']
        },
        styles: {
          backgroundColor: 'rgb(232, 237, 218)'
        }
      },
      OPERATIONAL_SJ_SZ: {
        displayName: 'SJ/SZ *',
        format: 'number',
        tooltip: [{
          column: 'WOW_OPERATIONAL_SJ_SZ',
          displayName: '%WOW',
          format: 'percentage'
        }],
        background: {
          comparedColumn: 'WOW_OPERATIONAL_SJ_SZ',
          valueRange: [-0.05, 0.05],
          colorRange: ['F1BFC1', 'C5DABF']
        },
        styles: {
          backgroundColor: 'rgb(232, 237, 218)'
        }
      },
      SICK: {
        displayName: '# of Sick Vehicles *',
        format: 'number',
        tooltip: [{
          column: 'WOW_SICK',
          displayName: '%WOW',
          format: 'percentage'
        }],
        background: {
          comparedColumn: 'WOW_SICK',
          valueRange: [-0.05, 0.05],
          colorRange: ['C5DABF', 'F1BFC1']
        },
        styles: {
          backgroundColor: 'rgb(232, 237, 218)'
        }
      },
      DECAYED: {
        displayName: '# of Decayed Vehicles *',
        format: 'number',
        tooltip: [{
          column: 'WOW_DECAYED',
          displayName: '%WOW',
          format: 'percentage'
        }],
        background: {
          comparedColumn: 'WOW_DECAYED',
          valueRange: [-0.05, 0.05],
          colorRange: ['C5DABF', 'F1BFC1']
        },
        styles: {
          backgroundColor: 'rgb(232, 237, 218)'
        }
      },
      OPS_REVIVED: {
        displayName: '# of Ops Revived Vehicles *',
        format: 'number',
        tooltip: [{
          column: 'WOW_OPS_REVIVED',
          displayName: '%WOW',
          format: 'percentage'
        }],
        background: {
          comparedColumn: 'WOW_OPS_REVIVED',
          valueRange: [-0.05, 0.05],
          colorRange: ['F1BFC1', 'C5DABF']
        },
        styles: {
          backgroundColor: 'rgb(232, 237, 218)'
        }
      }
    },
    additionalColumns: [
      {
        name: 'Fleet Health',
        span: 9,
        styles: {
          backgroundColor: 'rgb(232, 237, 218)'
        }
      }
    ],
    chart: {
      type: 'line',
      width: 150,
      xAxis: 'CALENDAR_DATE'
    },
    value: []
  }, {
    x: 0,
    y: 50,
    w: 20,
    h: 9.5,
    i: 12,
    metadata: {
      name: 'riderTable',
      title: 'Rider Table',
      type: 'table',
      customField: false,
      tableType: 'horizontal',
      scroll: true
    },
    headerColumn: {
      name: 'CALENDAR_DATE',
      displayName: 'Calendar Date',
      format: 'date',
      fromFormat: 'YYYY-MM-DD',
      toFormat: 'MM/DD (ddd)'
    },
    columns: {
      UNIQUE_ACTIVE_USERS: {
        displayName: 'Active Riders',
        format: 'number',
        tooltip: [{
          column: 'WOW_UNIQUE_ACTIVE_USERS',
          displayName: '%WOW',
          format: 'percentage'
        }],
        background: {
          comparedColumn: 'WOW_UNIQUE_ACTIVE_USERS',
          valueRange: [-0.05, 0.05],
          colorRange: ['F1BFC1', 'C5DABF']
        },
        styles: {
          backgroundColor: 'rgb(243, 235, 243)'
        }
      },
      UNIQUE_USER_SIGNUPS: {
        displayName: 'Number of Signups',
        format: 'number',
        tooltip: [{
          column: 'WOW_UNIQUE_USER_SIGNUPS',
          displayName: '%WOW',
          format: 'percentage'
        }],
        background: {
          comparedColumn: 'WOW_UNIQUE_USER_SIGNUPS',
          valueRange: [-0.05, 0.05],
          colorRange: ['F1BFC1', 'C5DABF']
        },
        styles: {
          backgroundColor: 'rgb(243, 235, 243)'
        }
      },
      UNIQUE_RIDER_FIRST_TRIPS: {
        displayName: 'Number of First Trips',
        format: 'number',
        tooltip: [{
          column: 'WOW_UNIQUE_RIDER_FIRST_TRIPS',
          displayName: '%WOW',
          format: 'percentage'
        }],
        background: {
          comparedColumn: 'WOW_UNIQUE_RIDER_FIRST_TRIPS',
          valueRange: [-0.05, 0.05],
          colorRange: ['F1BFC1', 'C5DABF']
        },
        styles: {
          backgroundColor: 'rgb(243, 235, 243)'
        }
      },
      TRIPS_PER_RIDER: {
        displayName: 'Trips Per Rider',
        format: 'decimal',
        tooltip: [{
          column: 'WOW_TRIPS_PER_RIDER',
          displayName: '%WOW',
          format: 'percentage'
        }],
        background: {
          comparedColumn: 'WOW_TRIPS_PER_RIDER',
          valueRange: [-0.05, 0.05],
          colorRange: ['F1BFC1', 'C5DABF']
        },
        styles: {
          backgroundColor: 'rgb(243, 235, 243)'
        }
      },
      AVG_TRIP_RATINGS: {
        displayName: 'Avg. Trip Star Rating',
        format: 'decimal',
        tooltip: [{
          column: 'WOW_AVG_TRIP_RATINGS',
          displayName: '%WOW',
          format: 'percentage'
        }],
        background: {
          comparedColumn: 'WOW_AVG_TRIP_RATINGS',
          valueRange: [-0.05, 0.05],
          colorRange: ['F1BFC1', 'C5DABF']
        },
        styles: {
          backgroundColor: 'rgb(243, 235, 243)'
        }
      }
    },
    additionalColumns: [
      {
        name: 'Rider',
        span: 5,
        styles: {
          backgroundColor: 'rgb(243, 235, 243)'
        }
      }
    ],
    chart: {
      type: 'line',
      width: 150,
      xAxis: 'CALENDAR_DATE'
    },
    value: []
  }, {
    x: 0,
    y: 62,
    w: 20,
    h: 6.5,
    i: 13,
    metadata: {
      name: 'juicerTable',
      title: 'Juicer Table',
      type: 'table',
      customField: false,
      tableType: 'horizontal',
      scroll: true
    },
    headerColumn: {
      name: 'CALENDAR_DATE',
      displayName: 'Calendar Date',
      format: 'date',
      fromFormat: 'YYYY-MM-DD',
      toFormat: 'MM/DD (ddd)'
    },
    columns: {
      PAYOUT_PER_TRIP: {
        displayName: 'Juicer Payout Per Trip *',
        format: 'currencyDecimal',
        tooltip: [{
          column: 'WOW_PAYOUT_PER_TRIP',
          displayName: '%WOW',
          format: 'percentage'
        }],
        background: {
          comparedColumn: 'WOW_PAYOUT_PER_TRIP',
          valueRange: [-0.05, 0.05],
          colorRange: ['C5DABF', 'F1BFC1']
        },
        styles: {
          backgroundColor: 'rgb(223, 231, 232)'
        }
      },
      PAYOUT_PER_CHARGE: {
        displayName: 'Avg Juicer Payout Per Charge *',
        format: 'currencyDecimal',
        tooltip: [{
          column: 'WOW_PAYOUT_PER_CHARGE',
          displayName: '%WOW',
          format: 'percentage'
        }],
        background: {
          comparedColumn: 'WOW_PAYOUT_PER_CHARGE',
          valueRange: [-0.05, 0.05],
          colorRange: ['C5DABF', 'F1BFC1']
        },
        styles: {
          backgroundColor: 'rgb(223, 231, 232)'
        }
      },
      PAYOUT_PER_RETRIEVAL: {
        displayName: 'Avg Juicer Payout Per Retrieval *',
        format: 'currencyDecimal',
        tooltip: [{
          column: 'WOW_PAYOUT_PER_RETRIEVAL',
          displayName: '%WOW',
          format: 'percentage'
        }],
        background: {
          comparedColumn: 'WOW_PAYOUT_PER_RETRIEVAL',
          valueRange: [-0.05, 0.05],
          colorRange: ['F1BFC1', 'C5DABF']
        },
        styles: {
          backgroundColor: 'rgb(223, 231, 232)'
        }
      }
    },
    additionalColumns: [
      {
        name: 'Juicer',
        span: 3,
        styles: {
          backgroundColor: 'rgb(223, 231, 232)'
        }
      }
    ],
    chart: {
      type: 'line',
      width: 150,
      xAxis: 'CALENDAR_DATE'
    },
    value: []
  }, {
    x: 0,
    y: 70,
    w: 20,
    h: 6.5,
    i: 14,
    metadata: {
      name: 'bottomlineTable',
      title: 'Bottomline Table',
      type: 'table',
      customField: false,
      tableType: 'horizontal',
      scroll: true
    },
    headerColumn: {
      name: 'CALENDAR_DATE',
      displayName: 'Calendar Date',
      format: 'date',
      fromFormat: 'YYYY-MM-DD',
      toFormat: 'MM/DD (ddd)'
    },
    columns: {
      NET_DEPRECIATION: {
        displayName: 'Net Depreciation **',
        format: 'currency',
        tooltip: [{
          column: 'WOW_NET_DEPRECIATION',
          displayName: 'Net Depreciation - WOW',
          format: 'percentage'
        }],
        background: {
          comparedColumn: 'WOW_NET_DEPRECIATION',
          valueRange: [-0.05, 0.05],
          colorRange: ['C5DABF', 'F1BFC1']
        },
        styles: {
          backgroundColor: 'rgb(239, 235, 232)'
        }
      },
      JUICER_LABOR_COST: {
        displayName: 'Juicer Labor Cost **',
        format: 'currency',
        tooltip: [{
          column: 'WOW_JUICER_LABOR_COST',
          displayName: 'Juicer Labor Cost - WOW',
          format: 'percentage'
        }],
        background: {
          comparedColumn: 'WOW_JUICER_LABOR_COST',
          valueRange: [-0.05, 0.05],
          colorRange: ['C5DABF', 'F1BFC1']
        },
        styles: {
          backgroundColor: 'rgb(239, 235, 232)'
        }
      },
      LOCAL_OPS_PAYOUT: {
        displayName: 'Local Ops Payout **',
        format: 'currency',
        tooltip: [{
          column: 'WOW_LOCAL_OPS_PAYOUT',
          displayName: 'Local Ops Payout - WOW',
          format: 'percentage'
        }],
        background: {
          comparedColumn: 'WOW_LOCAL_OPS_PAYOUT',
          valueRange: [-0.05, 0.05],
          colorRange: ['C5DABF', 'F1BFC1']
        },
        styles: {
          backgroundColor: 'rgb(239, 235, 232)'
        }
      }
    },
    additionalColumns: [
      {
        name: 'Bottomline',
        span: 3,
        styles: {
          backgroundColor: 'rgb(239, 235, 232)'
        }
      }
    ],
    chart: {
      type: 'line',
      width: 150,
      xAxis: 'CALENDAR_DATE'
    },
    value: []
  }, {
    x: 0,
    y: 17,
    w: 20,
    h: 1,
    i: 15,
    metadata: {
      type: 'blank',
      customField: true
    }
  }, {
    x: 0,
    y: 29,
    w: 20,
    h: 1,
    i: 16,
    metadata: {
      type: 'blank',
      customField: true
    }
  }, {
    x: 0,
    y: 49,
    w: 20,
    h: 1,
    i: 16,
    metadata: {
      type: 'blank',
      customField: true
    }
  }, {
    x: 0,
    y: 60,
    w: 20,
    h: 1,
    i: 17,
    metadata: {
      type: 'blank',
      customField: true
    }
  }, {
    x: 0,
    y: 69,
    w: 20,
    h: 1,
    i: 18,
    metadata: {
      type: 'blank',
      customField: true
    }
  }]
}
</script>
